import { ReactNode } from 'react';
import clsx from 'clsx';
import { ReadonlyHint, ReadonlyLock } from '@srm/ui/readonly-indicator';
import Checkbox from '@srm/ui/checkbox';
import DisplayField from '@molecules/DisplayField/DisplayField';
import AbookCellView from './AbookCellView';
import { parseDisplayFormatCell } from '@utils/documentUtils';
import { buildAbookCellPairs } from '@utils/tableHelper';
import {
    isTableColumn,
    isTableColumnAbook,
    isTableColumnAutoComplete,
    isTableColumnCalc,
    isTableColumnDict,
    isTableColumnUrl,
} from '@utils/tableColumnGuards';
import { ITableColumn, TableColumn } from '@models/Forms/IForms';
import { resolveColumnMaxLength } from '@utils/validation/document/tableValidation';
import { ValueType } from '@srm/ui/types/ValueType';
import { RouteNames } from '@/router/router.types';
import { splitLinkAddresses } from '@molecules/SystemLink/splitLinkAddresses';
import { TableRow } from './tableColumns';

export interface ICellRules {
    readonly?: boolean;
    required?: boolean;
    styleClasses?: string;
}

export interface ICellRenderDeps {
    /** Прекэшированные правила строки+колонки (readonly/required/стили-формулы). */
    rulesFor: (row: TableRow, colKey: string) => ICellRules | undefined;
    /** Мгновенный коммит (Boolean-чекбокс в ячейке — паритет showEditorAlways DX). */
    onInstantCommit: (row: TableRow, field: string, value: unknown) => void;
    skipRequired: boolean;
    skipPattern: boolean;
    skipLength: boolean;
    /** Режим просмотра: контент без подсветки invalid/readonly, Boolean-чекбокс
     *  отображает значение и не коммитит. */
    displayOnly: boolean;
    /** Постоянный запрет изменения всей таблицы (не временное ожидание загрузки). */
    readOnly?: boolean;
}

/** Display-содержимое ячейки — паритет cellRenderSwitcher старого EditTable. */
const cellContent = (
    column: TableColumn,
    columnId: string,
    row: TableRow,
    deps: ICellRenderDeps,
    readOnly: boolean,
): ReactNode => {
    const value = row[column.key];
    // dict-first: у dict-колонки может быть и tabs (ложно сработал бы abook-guard)
    if (isTableColumnDict(column)) {
        return <div>{parseDisplayFormatCell(column.displayFormat, row, column.evalDisplayFormat)}</div>;
    }
    if (isTableColumnAbook(column)) {
        return <AbookCellView column={column} p={{ data: row, value }} />;
    }
    if (isTableColumnCalc(column)) {
        return <DisplayField type={column.valueType ?? ValueType.Integer} value={value as never} />;
    }
    if (isTableColumnAutoComplete(column)) {
        return <DisplayField type={ValueType.Text} value={value as never} />;
    }
    if (isTableColumnUrl(column)) {
        const href = parseDisplayFormatCell(column.href, row);
        return (
            <DisplayField
                type={ValueType.Url}
                value={parseDisplayFormatCell(column.displayFormat, row) as never}
                // Как у поля <url>: hrefIsDocId — в адресе ключи документов, по одному на строку.
                href={
                    column.hrefIsDocId && href
                        ? splitLinkAddresses(String(href))
                              .map((id) => RouteNames.DOCUMENT.replace(':id', id))
                              .join('\n')
                        : href
                }
            />
        );
    }
    const col = column as ITableColumn;
    if (col.valueType === ValueType.Boolean) {
        // мгновенное редактирование чекбоксом прямо в ячейке (showEditorAlways DX);
        // в просмотре — только индикатор значения
        const locked = readOnly || deps.displayOnly;
        return (
            <Checkbox
                readOnly={locked}
                disabled={locked}
                name={col.key}
                aria-label={col.name}
                checked={value === 1 || value === '1'}
                onCheckedChange={(checked) => {
                    if (!locked) deps.onInstantCommit(row, columnId, checked ? 1 : 0);
                }}
            />
        );
    }
    return (
        <DisplayField type={col.valueType} value={value as never} trueText={col.trueText} falseText={col.falseText} />
    );
};

/** Видимый текст ячейки — то же, что показывает рендер: справочник, адресная книга
 *  и ссылка разворачивают displayFormat, остальное — значение.
 *  Нужен header-фильтру (список значений) и выгрузке в Excel. */
export const cellTextOf = (column: TableColumn, row: TableRow): string => {
    const value = row[column.key];
    if (isTableColumnAbook(column)) {
        // несколько сотрудников ячейка показывает через «, » — так же и в фильтре
        const pairs = buildAbookCellPairs(column, { data: row, value });
        if (pairs) return pairs.map((pair) => pair.name).join(', ');
    }
    if (isTableColumnDict(column) || isTableColumnAbook(column)) {
        return String(parseDisplayFormatCell(column.displayFormat, row, column.evalDisplayFormat) ?? '');
    }
    if (isTableColumnUrl(column)) {
        return String(parseDisplayFormatCell(column.displayFormat, row) ?? '');
    }
    const col = column as ITableColumn;
    if (col.valueType === ValueType.Boolean) {
        return value === 1 || value === '1' ? (col.trueText ?? 'Да') : (col.falseText ?? 'Нет');
    }
    return value == null ? '' : String(value);
};

/** Состояние ячейки в редактировании:
 *  - invalid: обязательное пустое или неверный формат — красная рамка (как в легаси);
 *  - empty: пустое НЕобязательное — деликатный намёк «сюда можно вписать»;
 *  - blank: пустое readonly и НЕобязательное — заполнять нечем. */
export type TableCellState = 'invalid' | 'empty' | 'blank' | undefined;

const isEmptyValue = (value: unknown) =>
    value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '');

/** Кэш скомпилированных паттернов колонок: getCellState бежит на каждую ячейку
 *  каждого рендера, и повторная компиляция RegExp на этом пути — чистая трата.
 *  Битый паттерн конфигурации кэшируется как null и не валит рендер. */
const patternCache = new Map<string, RegExp | null>();
const compiledPattern = (pattern: string): RegExp | null => {
    let compiled = patternCache.get(pattern);
    if (compiled === undefined) {
        try {
            compiled = new RegExp(pattern);
        } catch {
            compiled = null;
        }
        patternCache.set(pattern, compiled);
    }
    return compiled;
};

export const getCellState = (
    column: TableColumn,
    row: TableRow,
    deps: ICellRenderDeps,
    rules: ICellRules | undefined,
    readOnly: boolean,
): TableCellState => {
    const col = column as ITableColumn;
    const value = row[col.key];
    // пустота и признак «заполнять нечем» считаются для ЛЮБОЙ колонки
    // (справочник, адресная книга, вычисляемая, ссылка), а не только для field:
    // иначе прочерк и линейка появлялись бы через одну колонку
    const empty = isEmptyValue(value);
    const boolean = 'valueType' in column && col.valueType === ValueType.Boolean;
    // required — у колонки любого вида, кроме ссылки и Boolean: ровно тот набор,
    // который проверяет validateDocumentTable. Иначе пустой обязательный
    // справочник/адресная книга валит вкладку, а ячейка остаётся неподсвеченной.
    const required =
        !deps.skipRequired &&
        !boolean &&
        !isTableColumnUrl(column) &&
        (col.required === 'true' || (!!col.required && col.required !== 'false' && rules?.required === true));
    // считается до readOnly: валидатор не смотрит на readonly, так что пустая
    // обязательная ячейка мешает сохранению независимо от того, можно ли её
    // править руками — прочерк «заполнять нечем» тут врал бы
    if (required && empty) return 'invalid';
    if (readOnly) return empty && !boolean ? 'blank' : undefined;
    // формат и длину проверяем только у колонок-полей — как и validateDocumentTable
    if (!isTableColumn(column)) return empty && !boolean ? 'empty' : undefined;
    if (!deps.skipPattern && col.pattern) {
        const pattern = compiledPattern(col.pattern);
        if (pattern && !pattern.test(String(value ?? ''))) return 'invalid';
    }
    if (!deps.skipLength && value != null && value !== '' && typeof value === 'string') {
        const maxLength = resolveColumnMaxLength(col);
        if (maxLength !== undefined && value.length > maxLength) return 'invalid';
        if (col.min && value.length < +col.min) return 'invalid';
    }
    // пустое необязательное поле — не проблема, но видно, что его можно заполнить
    return empty && !boolean ? 'empty' : undefined;
};

/** Рендер ячейки таблицы форм: display-контент + классы invalid/readonly.
 *  kindEditable=false — колонка нередактируема самим видом:
 *  визуально она readonly, даже без правила readonly в конфигурации. */
export const buildTableCellRender =
    (column: TableColumn, columnId: string, deps: ICellRenderDeps, kindEditable = true) =>
    (row: TableRow): ReactNode => {
        const col = column as ITableColumn;
        const displayOnly = deps.displayOnly || column.displayMode === 'view';
        const rules = deps.rulesFor(row, columnId);
        // непосчитанная формула = readonly (безопасный дефолт, зеркало editableOf):
        // для Boolean этот флаг — единственный гейт мгновенного чекбокса
        const readonlyFormula = !!col.readonly && col.readonly !== 'true' && col.readonly !== 'false';
        const readOnly = !!deps.readOnly || col.readonly === 'true' || (readonlyFormula && rules?.readonly !== false);
        // у ссылок свой облик (синий якорь) — приглушать их плашкой нельзя
        const visualReadOnly = readOnly || (!kindEditable && !isTableColumnUrl(column));
        const longText = col.valueType === ValueType.LongText || col.valueType === ValueType.Base64Text;
        const state = displayOnly ? undefined : getCellState(column, row, deps, rules, visualReadOnly);
        const resolvedReadOnly =
            !!deps.readOnly ||
            col.readonly === 'true' ||
            (readonlyFormula && rules?.readonly === true) ||
            !kindEditable;
        const showReadonly = !displayOnly && resolvedReadOnly && !isTableColumnUrl(column);
        const readonlyHint = showReadonly && col.valueType === ValueType.Boolean;
        const readonlyMark = showReadonly && !readonlyHint;
        return (
            <ReadonlyHint disabled={!readonlyHint}>
                <div
                    aria-label={readonlyHint ? col.name : undefined}
                    onClick={readonlyHint ? (event) => event.stopPropagation() : undefined}
                    onKeyDown={
                        readonlyHint
                            ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                  }
                              }
                            : undefined
                    }
                    // styleClasses — глобальные css-классы стилей-формул конфигурации (column.styles)
                    className={clsx('tdn-cell', rules?.styleClasses, {
                        // свои классы: глобальные .invalid-cell/.readonly-cell легаси-грида
                        // рисуют бордеры под другую разметку; в просмотре подсветка не нужна
                        'tdn-cell--invalid': state === 'invalid',
                        'tdn-cell--empty': state === 'empty',
                        'tdn-cell--readonly': readonlyMark,
                        'tdn-cell--readonly-hint': readonlyHint,
                        'tdn-cell--longtext': longText,
                    })}
                >
                    {/* пустая readonly-ячейка получает прочерк: «нечего заполнять»
                    не должно читаться так же, как «ещё не заполнено» */}
                    {state === 'blank' ? (
                        <span className="tdn-cell__blank" aria-label="Не заполняется">
                            —
                        </span>
                    ) : (
                        cellContent(column, columnId, row, deps, readOnly || displayOnly)
                    )}
                    {readonlyMark && <ReadonlyLock className="tdn-cell__lock" />}
                </div>
            </ReadonlyHint>
        );
    };
